export const name = 'gzPageHeader';

/**
 * @ngdoc directive
 * @name gazelle.components.pageHeader.directive:gzPageHeader
 *
 * @param {string=} title. Specify the Page title
 *
 * @description
 * `<gz-page-header>` is a widget that places a page header for the current page. A page-header is a title bar that contains
 * bread-crumb navigation, a page title, and optional tool-bar content.
 *
 * @usage
 * ```html
 * <gz-page-header
 *   page-title="{{'pageTitle' | translate}}"
 *   parent-state="{{ui-router state to go back to}}"
 *   parent-description="{{'backArrowTooltip' | translate}}"
 *   breadcrumbs="array of breadcrumb descriptions"
 *   support-task="true to allow header to switch between Task Mode and Normal Mode"
 *   is-full-app="true to show the app navigation by default"
 *   submit-states="(OPTIONAL) array of state names to allow submit.  Will assume entry point state if not provided"
 *   hide-back-to-task="(OPTIONAL) true to suppress the 'Back to Task' button on the task bar"
 *   hide-exit-task="(OPTIONAL) true to suppress the 'Exit Task' button on the task bar"
 *   responsive-mode="'collapse' or 'row', default is none.  'collapse' will collapse into a sub-menu where 'row' will move menu to new row under responsive"
 *   task-title="{{'taskTitleToUseInTaskMode' | translate}}"
 *   task-submit="controller.submit function that returns promise which returns payload (REQUIRED if support-task='true') "
 *   task-submit-title="{{'submitButtonTitleOverride' | translate}}"
 *   task-cancel="controller.cancel function that returns promise"
 *   task-exit="controller.exit function that returns promise for clean up of any application task data"
 *   task-disable-submit="controller.disableSubmit function that returns boolean"
 * >
 *  <span layout='row'>
 *    <i class='toolbar-icon fa fa-plus fa-2x' ...></i>
 *    <i class='toolbar-icon fa fa-edit fa-2x' ...></i>
 *  </span>
 *
 * ```
 */
export default function gzPageHeader($log, $state) {
  //noinspection BadExpressionStatementJS
  'ngInject';


  var directive = {
    restrict: 'EA',
    transclude: true,
    scope: {
      pageTitle: '@',
      parentState: '@',
      parentStateParams: '=?',
      parentDescription: '@',
      breadcrumbs: '=?',
      supportTask: '@',
      submitStates: '=?',
      isFullApp: '=?',
      hideBackToTask: '=?',
      hideExitTask: '=?',
      hideCancel: '=?',
      responsiveMode: '@',
      taskTitle: '@',
      taskSubmit: '=?',
      taskSubmitTitle: '@',
      taskCancel: '=?',
      taskExit: '=?',
      taskDisableSubmit: '=?'
    },
    template: require('./page-header.jade'),
    link: gzPageHeaderLink,
    controller: gzPageHeaderController,
    controllerAs: 'vm',
    bindToController: true
  };

  return directive;

  //////////////////////
  // Private functions
  function gzPageHeaderLink(scope, element, attrs) {
    element.attr({
      'role' : 'header'
    });
    if (attrs.pageTitle === undefined) {
      $log.warn('pageTitle is undefined. Set the page title using the page-title attribute.');
      attrs.pageTitle = attrs.title;
      element.removeAttr('title');
    }
    if (attrs.title !== undefined) {
      $log.warn('Using title to set the page title has been deprecated. Please use page-title.');
    }
  }

  /** @ngInject */
  function gzPageHeaderController($scope, $mdMedia, $gzGo, $translate, rnClient, rnApplication, $state) {
    var vm = this;

    let showFullAppTitle;
    let hideFullAppTitle;

    vm.$mdMedia = $mdMedia;
    vm.$gzGo = $gzGo;
    vm.getPageTitle = getPageTitle;
    vm.cancel = cancel;
    vm.submit = submit;
    vm.taskSource = null;
    vm.toggleAppNav = toggleAppNav;
    vm.getShowHideTitle = getShowHideTitle;
    vm.appNavShown = appNavShown;
    vm.exitTask = exitTask;
    vm.isLoading = true;
    vm.showToolbar = false;
    vm.isResponsive = false;
    vm.isCollapse = false;
    vm.isTask = false;
    vm.goToTask = goToTask;
    vm.gotoParent = gotoParent;
    vm.gotoState = gotoState;

    // Code here to manage/manipulate/respond to the breadcrumb links

    vm.$onInit = onInit;

    //////////////////

    function onInit() {
      if (vm.responsiveMode) {
        if (vm.responsiveMode === "collapse") {
          vm.isResponsive = true;
          vm.isCollapse = true;
        }
        else if (vm.responsiveMode === "row") {
          vm.isResponsive = true;
          vm.isCollapse = false;
        }
      }

      if (vm.isFullApp) {
        $gzGo.setAppNav(true);
      }

      let promises = [];
      promises.push(initTask());
      return Promise.all(promises);
    }

    function initTask() {
      return $gzGo.taskIsActive().then((isActive) => {
        vm.taskData = $gzGo.getTaskData();
        vm.hideTaskBar = _.get(vm.taskData, 'payload.hideTaskBar', false);
        vm.allowExitToApp = _.get(vm.taskData, 'payload.allowExitToApp', true);
        vm.allowAppNav = _.get(vm.taskData, 'payload.allowAppNav', true);
        vm.returnLabel = _.get(vm.taskData, 'payload.returnLabel');
        if (vm.taskDisableSubmit) {
          vm.disableSubmit = vm.taskDisableSubmit;
        }
        else {
          vm.disableSubmit = defaultDisableSubmit;
        }

        if (isActive) {
          vm.isTask = true;
          if (vm.submitStates) {
            vm.isSubmitPage = vm.submitStates.indexOf($state.current.name) > -1;
          }
          else {
            vm.isSubmitPage = $gzGo.getTaskId() === $state.current.name;
          }

          return rnApplication.getAppLocName(vm.taskData.appId).then((name) => {
            vm.taskSource = vm.returnLabel ? vm.returnLabel : name;
            return rnApplication.getAppLocName(rnApplication.getCurrentAppId()).then((name) => {
              showFullAppTitle = $translate.instant('uxf.uxf.Show_FullApp', [name]);
              hideFullAppTitle = $translate.instant('uxf.uxf.Hide_FullApp', [name]);
              vm.exitToApplicationTitle = $translate.instant('uxf.uxf.Exit_Task', [name]);
            }).finally(() => {
              vm.isLoading = false;
            });
          });
        }
        else {
          vm.isLoading = false;
        }
      });
    }

    function getPageTitle() {
      if (vm.taskTitle) {
        return vm.taskTitle();
      }

      return vm.pageTitle;
    }

    function gotoParent() {
      $state.go(vm.parentState, vm.parentStateParams);
    }

    function gotoState(state, stateParams) {
      $state.go(state, stateParams);
    }

    function getShowHideTitle() {
      if ($gzGo.showAppNav()) {
        return hideFullAppTitle;
      }
      else {
        return showFullAppTitle;
      }
    }

    function appNavShown() {
      return $gzGo.showAppNav;
    }

    function cancel() {
      if(vm.taskCancel) {
        vm.taskCancel().then(() => {
          $gzGo.cancelTask();
        });
      }
      else {
        $gzGo.cancelTask();
      }
    }

    function submit() {
      if (vm.taskSubmit) {
        vm.taskSubmit().then((payload) => {
          $gzGo.completeTask(payload);
        });
      }
      else {
        $gzGo.completeTask({});
      }
    }

    function toggleAppNav() {
      $gzGo.toggleAppNav();
    }

    function goToTask() {
      $gzGo.goToTaskHome();
    }

    function exitTask() {
      if (vm.taskExit) {
        vm.taskExit().then(() => {
          $gzGo.exitTask();
          vm.isTask = false;
          vm.isSubmitPage = false;
        });
      }
      else {
        $gzGo.exitTask();
        vm.isTask = false;
        vm.isSubmitPage = false;
      }
    }

    function defaultDisableSubmit() {
      return false;
    }
  }
}
